import { useState, useEffect, useRef, useCallback } from "react";

interface ServerSearchResult {
  type: "article" | "guide";
  title: string;
  slug: string;
  excerpt: string;
  category: string;
  url: string;
  publishedAt?: string;
  readTime?: string;
  tag?: string;
}

interface ServerSearchResponse {
  results: ServerSearchResult[];
  total: number;
  query: string;
  limit: number;
  offset: number;
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

export function useServerSearch(query: string, debounceMs = 250) {
  const [results, setResults] = useState<ServerSearchResult[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout>>();

  const search = useCallback(
    (q: string) => {
      // Cancel in-flight request
      abortRef.current?.abort();

      if (!q.trim() || q.trim().length < 2) {
        setResults([]);
        setTotal(0);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      const controller = new AbortController();
      abortRef.current = controller;

      const url = `${SUPABASE_URL}/functions/v1/search?q=${encodeURIComponent(q.trim())}&limit=12`;

      fetch(url, {
        signal: controller.signal,
        headers: {
          apikey: SUPABASE_ANON_KEY,
          Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        },
      })
        .then((res) => res.json())
        .then((data: ServerSearchResponse) => {
          if (!controller.signal.aborted) {
            setResults(data.results || []);
            setTotal(data.total || 0);
            setIsLoading(false);
          }
        })
        .catch((err) => {
          if (err.name !== "AbortError") {
            setIsLoading(false);
            setResults([]);
            setTotal(0);
          }
        });
    },
    []
  );

  useEffect(() => {
    clearTimeout(timerRef.current);
    if (!query.trim()) {
      setResults([]);
      setTotal(0);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    timerRef.current = setTimeout(() => search(query), debounceMs);
    return () => {
      clearTimeout(timerRef.current);
      abortRef.current?.abort();
    };
  }, [query, debounceMs, search]);

  return { results, total, isLoading };
}

export type { ServerSearchResult };
